/* eslint-disable react-hooks/exhaustive-deps */
import React, { lazy, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

/** Utilities */
import { encryptUrlData } from "../../../services/CryptoService";
import { timesheetStatusEnums } from "../../../utils/Enums/timesheetStatusEnums";

/** Common Components */
import Buttons from "../../../components/ui/button/Buttons";
import Accordion from "../../../components/accordion/Accordion";
import CardSection from "../../../components/ui/card/CardSection";
import CenterModel from "../../../components/ui/centerModel/CenterModel";
import DatePicker from "../../../components/ui/inputs/datePicker/DatePicker";

/** Common Services & Data files */
import formatDate from "../../../lib/formatDate";
import { getCardInfo } from "../../../lib/getCardInfo";
import SwalAlert from "../../../services/swalService/SwalService";
import ToastService from "../../../services/toastService/ToastService";

/** RTK Query */
import { useLazyGetOrganizationWorkScheduleByOrgIdQuery } from "../../../app/services/organizationAPI";
import {
  useDeleteTimesheetDetailMutation,
  useLazyGetAllTimeSheetRecordsQuery,
  useLazyGetTimeSheetStatusQuery,
  useSubmitTimesheetMutation,
} from "../../../app/services/timeSheetLogAPI";

/** Lazily Loaded Components */
const AddUpdateTimeLog = lazy(() => import("../../../components/addUpdateTimelog/AddUpdateTimeLog"));

const MyTimesheet = () => {
  const UserId = useSelector((state) => state.auth.user.userId);
  const organizationId = useSelector(
    (state) => state.auth.user?.organizationId
  );

  const location = useLocation();
  const [initialStartDate] = useState(
    () => location.state?.startDate || new Date()
  );
  const [initialEndDate] = useState(
    () => location.state?.endDate || new Date()
  );

  const initialStatus = { statusId: 0, statusType: "" };

  const [timesheetList, setTimesheetList] = useState(null);
  const [weekDate, setWeekDate] = useState(new Date());
  const [s_Date, setS_Date] = useState(new Date());
  const [e_Date, setE_Date] = useState(new Date());
  const [startDate, setstartDate] = useState();
  const [workSchedule, setworkSchedule] = useState();
  const [timesheetStatus, setTimesheetStatus] = useState(initialStatus);
  // const [isTimesheetEditable, setIsTimesheetEditable] = useState(false);
  const [newEditData, setNewEditData] = useState(null);
  const [newModelOpen, setNewModelOpen] = useState(false);

  const { confirm } = SwalAlert();

  const [getAllTimeSheetRecords, {
    isLoading: isAllRecordsLoading,
    isFetching: isAllRecordsFetching,
    isSuccess: isAllRecordsSuccess,
    data: timeSheetRecordsData,
  },] = useLazyGetAllTimeSheetRecordsQuery();

  const [deleteTimesheetDetail, {
    isSuccess: isdeleteTimesheetDetailSuccess,
    data: deleteTimesheetDetailData,
  },] = useDeleteTimesheetDetailMutation();

  const [getOrganizationWorkScheduleByOrgId, {
    isFetching: isOrgWorkScheduleFetching,
    isSuccess: isOrgWorkScheduleSuccess,
    data: isOrgWorkScheduleData,
  },] = useLazyGetOrganizationWorkScheduleByOrgIdQuery();

  const [submitTimesheet, {
    isLoading: issubmitTimesheetLoading,
    isSuccess: issubmitTimesheetSuccess,
    data: submitTimesheetData,
  },] = useSubmitTimesheetMutation();

  const [getTimeSheetStatus, {
    isLoading: isgetTimeSheetStatusLoading,
    isFetching: isgetTimeSheetStatusFetching,
    isSuccess: isgetTimeSheetStatusSuccess,
    data: isgetTimeSheetStatusData,
  },] = useLazyGetTimeSheetStatusQuery();

  useEffect(() => {
    if (organizationId && UserId && startDate) {
      getOrganizationWorkScheduleByOrgId(organizationId);
      let request = {
        userId: UserId,
        startDate: startDate,
      };
      getTimeSheetStatus(request);
    }
  }, [organizationId, UserId, startDate]);

  useEffect(() => {
    if (!isOrgWorkScheduleFetching && isOrgWorkScheduleSuccess && isOrgWorkScheduleData) {
      setworkSchedule(isOrgWorkScheduleData);
    }
  }, [isOrgWorkScheduleFetching, isOrgWorkScheduleSuccess, isOrgWorkScheduleData,]);

  useEffect(() => {
    if (!isgetTimeSheetStatusFetching && isgetTimeSheetStatusSuccess && isgetTimeSheetStatusData) {
      setTimesheetStatus(isgetTimeSheetStatusData);
    } else {
      setTimesheetStatus(initialStatus);
    }
  }, [isgetTimeSheetStatusFetching, isgetTimeSheetStatusSuccess, isgetTimeSheetStatusData,]);

  const getTimeSheetList = (date) => {
    setstartDate(date);
    let request = {
      userId: UserId,
      startDate: date,
    };
    getAllTimeSheetRecords(request);
  };

  const getStartOfWeek = (date) => {
    const day = date.getDay(); // 0 (Sunday) to 6 (Saturday)
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(date.setDate(diff));
  };

  const getEndOfWeek = (date) => {
    const day = date.getDay(); // 0 (Sunday) to 6 (Saturday)
    const diff = date.getDate() - day + (day === 0 ? -6 : 1) + 6; // Adjust and add 6 days to get Saturday
    return new Date(date.setDate(diff));
  };

  const handleDateChange = (date) => {
    if (date) {
      setWeekDate(date);
      setS_Date(getStartOfWeek(new Date(date)));
      setE_Date(getEndOfWeek(new Date(date)));
    } else {
      setWeekDate(new Date());
    }
  };

  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      setS_Date(getStartOfWeek(new Date(initialStartDate)));
      setE_Date(getEndOfWeek(new Date(initialEndDate)));
      setWeekDate(new Date(initialStartDate));
    } else {
      setS_Date(getStartOfWeek(new Date()));
      setE_Date(getEndOfWeek(new Date()));
      setWeekDate(new Date());
    }
  }, []);

  const setWeekData = (date) => {
    setS_Date(getStartOfWeek(new Date(date)));
    setE_Date(getEndOfWeek(new Date(date)));
  };

  const isCurrentDateInWeek = (currentDate, weekStartDate) => {
    const startOfWeek = new Date(weekStartDate);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return currentDate >= startOfWeek && currentDate <= endOfWeek;
  };

  const handleNextWeekClick = () => {
    const currentWeekStartDate = getStartOfWeek(new Date(weekDate)); // Start of current week
    const nextWeekStartDate = new Date(
      currentWeekStartDate.setDate(currentWeekStartDate.getDate() + 7)
    ); // Add 7 days
    setWeekDate(nextWeekStartDate);
  };

  const handleTodayClick = () => {
    setWeekDate(new Date());
  };

  const handlePrevWeekClick = () => {
    const currentWeekStartDate = getStartOfWeek(new Date(weekDate)); // Start of current week
    const prevWeekStartDate = new Date(
      currentWeekStartDate.setDate(currentWeekStartDate.getDate() - 7)
    ); // Subtract 7 days
    if (weekDate.getTime() !== prevWeekStartDate.getTime()) {
      setWeekDate(prevWeekStartDate);
    }
  };

  const totalOrganizationHours = workSchedule?.workingDays * workSchedule?.workingHoursPerDay; const workingHoursPerDay = workSchedule?.workingHoursPerDay;

  const result = (timesheetList || []).map((hours) => {
    const dailyWorkingHours = parseFloat(hours.totalWorkingHours); // Convert to float
    const isSufficient = dailyWorkingHours >= workingHoursPerDay;

    return {
      date: hours.startDate,
      dailyWorkingHours,
      isSufficient,
    };
  });

  const totalTimesheetworkingHours = timesheetList?.reduce((acc, curr) => {
    return acc + parseFloat(curr.totalWorkingHours);
  }, 0);

  const handlesubmitClick = () => {
    const timesheetIdsString = timesheetList
      .map((item) => item.timesheetId)
      .join(", ");
    const firstName = timesheetList[0]?.taskList[0]?.firstName;
    const lastName = timesheetList[0]?.taskList[0]?.lastName;
    const emailAddress = timesheetList[0]?.taskList[0]?.emailAddress;
    submitTimesheet({
      selectedTimesheetsId: timesheetIdsString.split(",")[0],
      userId: UserId,
      emailAddress: emailAddress,
      firstName: firstName,
      lastName: lastName,
    });
  };

  useEffect(() => {
    if (!issubmitTimesheetLoading && issubmitTimesheetSuccess && submitTimesheetData) {
      ToastService.success(submitTimesheetData.errorMessage);
      getOrganizationWorkScheduleByOrgId(organizationId);
      let req = {
        userId: UserId,
        startDate: startDate,
      };
      getTimeSheetStatus(req);
    }
  }, [issubmitTimesheetLoading, issubmitTimesheetSuccess, submitTimesheetData]);

  const getLastEndTimeForDay = (list, date) => {
    if (!list?.length || !date) return null;

    const targetDate = formatDate(new Date(date), "YYYY-MM-DD");
    const dayData = list.find(
      (day) => formatDate(new Date(day.startDate), "YYYY-MM-DD") === targetDate
    );
    const taskList = dayData?.taskList;
    if (!taskList?.length) return null;

    return taskList[taskList.length - 1]?.endTime || null;
  };

  const handleNewAddClick = () => {
    setNewModelOpen(true);
  };

  const handleDeleteClick = (data) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteTimesheetDetail(data.timesheetDetailId);
      }
    });
  };

  const filterPassedDate = (date) => {
    if (!weekDate) return true;
    const currentDate = new Date();
    const selectedDate = new Date(date);
    return selectedDate.getTime() <= currentDate.getTime();
  };

  useEffect(() => {
    if (isdeleteTimesheetDetailSuccess && deleteTimesheetDetailData) {
      ToastService.success(deleteTimesheetDetailData.errorMessage);
      getTimeSheetList(startDate);
    }
  }, [isdeleteTimesheetDetailSuccess, deleteTimesheetDetailData]);

  const handleTaskCodeClick = (data) => {
    const dataURL = {};
    dataURL.taskId = data.taskId || 5;
    dataURL.projectId = data.projectId || 146;
    window.open(`/project/EditTask/${encryptUrlData(dataURL)}}`, "_blank");
  };

  const handleSuccess = () => {
    handleNewModelCloseClick();
    let starDate = weekDate?.toISOString();
    if (starDate) {
      setWeekData(starDate);
      getTimeSheetList(starDate);
      let req = {
        userId: UserId,
        startDate: startDate,
      };
      getTimeSheetStatus(req);
      // setIsTimesheetEditable(true);
    }
  };

  const handleNewEditClick = (data) => {
    setNewModelOpen(true);
    setNewEditData(data);
  };

  const handleNewModelCloseClick = () => {
    setNewEditData(null);
    setNewModelOpen(false);
  };

  const currentDate = new Date();
  const currentWeekStartDate = getStartOfWeek(new Date(weekDate));
  const isTimesheetNavigationButtonDisabled = isCurrentDateInWeek(currentDate, currentWeekStartDate);

  useEffect(() => {
    let starDate = weekDate?.toISOString();
    if (starDate) {
      setWeekData(starDate);
      getTimeSheetList(starDate);
    }
  }, [weekDate]);

  useEffect(() => {
    if (!isAllRecordsFetching && isAllRecordsSuccess && timeSheetRecordsData) {
      setTimesheetList(timeSheetRecordsData);
    }
  }, [isAllRecordsFetching, isAllRecordsSuccess, timeSheetRecordsData]);

  const isTimesheetOpen = timesheetStatus?.statusId === timesheetStatusEnums.OPEN;
  const isTimesheetApproved = timesheetStatus?.statusId === timesheetStatusEnums.APPROVED;
  const isTimesheetSubmitted = timesheetStatus?.statusId === timesheetStatusEnums.SUBMITTED;
  const isTimesheetReSubmitted = timesheetStatus?.statusId === timesheetStatusEnums.RE_SUBMITTED;
  const isTimesheetReOpen = timesheetStatus?.statusId === timesheetStatusEnums.RE_OPEN;

  const hasEnoughWorkingHours = totalTimesheetworkingHours >= totalOrganizationHours;
  const allEntriesSufficient = result.every(item => item.isSufficient);

  const isSubmitButtonDisabled = !((isTimesheetOpen || isTimesheetReOpen) && hasEnoughWorkingHours && allEntriesSufficient);

  // Custom Header Content
  const headerContentWrapper = () => {
    return (
      <div className="timesheet-header-wrapper w-100">
        <div className="timesheet-header-left">
          <div className="mytimesheet-date">
            <DatePicker
              isDisable={false}
              selected={new Date(weekDate)}
              onChange={handleDateChange}
              placeholder={"select date"}
              onBlur={() => { }}
              datePickerConfig={{ filteredDate: filterPassedDate, format: "dd/MM/yyyy", }}
            />
          </div>
          <div className="timesheet-header-actions-left">
            <Buttons
              buttonText="Prev"
              textWithIcon={true}
              fontIcon={true}
              iconName={`fluent:arrow-previous-16-filled`}
              onClick={handlePrevWeekClick}
              buttonTypeClassName="theme-btn"
            />
            <Buttons
              buttonText="Current"
              textWithIcon={true}
              fontIcon={true}
              iconName={`ic:outline-today`}
              onClick={handleTodayClick}
              buttonTypeClassName="theme-btn"
            />
            <Buttons
              buttonText="Next"
              textWithIcon={true}
              fontIcon={true}
              iconName={`fluent:arrow-next-16-filled`}
              onClick={handleNextWeekClick}
              buttonTypeClassName="theme-btn"
              isDisable={isTimesheetNavigationButtonDisabled}
            />
            <Buttons
              buttonText={isTimesheetReOpen ? "Re Submit" : "Submit"}
              textWithIcon={true}
              fontIcon={true}
              className="submit-btn"
              iconName={`material-symbols-light:upload-file-outline`}
              isDisable={isSubmitButtonDisabled}
              onClick={() => handlesubmitClick()}
              buttonTypeClassName="theme-btn"
              isLoading={issubmitTimesheetLoading}
            />
            <Buttons
              buttonText="Add"
              fontIcon={true}
              textWithIcon={true}
              className="add-btn mobile-only-add"
              iconName={`ri:add-fill`}
              onClick={handleNewAddClick}
              isDisable={isTimesheetApproved || isTimesheetSubmitted || isTimesheetReSubmitted }
              buttonTypeClassName="theme-btn ml-2"
            />
          </div>
        </div>
        <div className="timesheet-header-right">
          <Buttons
            buttonText="Add"
            fontIcon={true}
            textWithIcon={true}
            className="add-btn desktop-only-add"
            iconName={`ri:add-fill`}
            onClick={handleNewAddClick}
            isDisable={isTimesheetApproved || isTimesheetSubmitted || isTimesheetReSubmitted }
            buttonTypeClassName="theme-btn ml-2"
          />
        </div>
      </div>
    );
  };

  const cardData = [
    {
      key: "Timesheet Summary",
      value: formatDate(s_Date, "DD MMM, YYYY") + " - " + formatDate(e_Date, "DD MMM, YYYY"),
    },
  ];

  return (
    <>
      <div className="card-section-info mt-2">
        {cardData ? getCardInfo(cardData) : null}
      </div>
      <div className="center-top-space">
        <CardSection
          isContentLoading={isAllRecordsLoading || isgetTimeSheetStatusLoading}
          customHeader={true}
          headerContentWrapper={headerContentWrapper()}
          cardTitle={`My Time Sheet`}
        >
          <div className="timesheet-accordion">
            <Accordion
              startDate={startDate}
              isLoading={isAllRecordsLoading}
              // onEdit={handleEditClick}
              onEdit={handleNewEditClick} // Testing Vaibhav
              onDelete={handleDeleteClick}
              onTaskCodeClick={handleTaskCodeClick}
              handleSuccess={handleSuccess}
              data={timesheetList}
              descriptionTooltipOnlyOnOverflow={true}
              isActionShow={isTimesheetOpen || isTimesheetReOpen}
            />
          </div>
        </CardSection>
      </div>
      <CenterModel
        showModal={newModelOpen}
        handleToggleModal={handleNewModelCloseClick}
        modalTitle="Timesheet details"
        className={"timelog-sidebar "}
        modelSizeClass="timesheet-model"
      >
        <AddUpdateTimeLog
          isOpen={newModelOpen}
          defaultDate={weekDate}
          defaultStartTime={getLastEndTimeForDay(timesheetList, weekDate)}
          onSuccess={handleSuccess}
          timesheetData={newEditData}
          onClose={handleNewModelCloseClick}
        />
      </CenterModel>
    </>
  );
};

export default MyTimesheet;
